export interface DesktopPaymentProduct {
	provider?: "oxapay" | "shopline";
  enabled: boolean;
  product?: string;
  amount?: number;
  currency?: string;
  payment_currency?: string;
  quote_amount?: number;
  quote_currency?: string;
  license_label?: string;
  sandbox?: boolean;
  licensing_enabled?: boolean;
  license_email_enabled?: boolean;
  trial_enabled?: boolean;
  trial_days?: number;
}

export interface DesktopPaymentOrder {
  order_id: string;
  product: string;
  amount: number;
  currency: string;
  display_amount?: number;
  display_currency?: string;
  license_label: string;
  status: "creating" | "waiting" | "paying" | "paid" | "expired" | "failed";
  sandbox: boolean;
  license_code?: string;
  license_revoked?: boolean;
  license_email_enabled?: boolean;
}

export interface DesktopCheckoutResult {
  order_id: string;
  order_token: string;
  payment_url: string;
  return_url: string;
}

export class DesktopPaymentError extends Error {
  constructor(public readonly code: string) { super(code); }
}

export async function paymentRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(path, { ...options, cache: "no-store" });
  const result = await response.json().catch(() => null);
  if (!response.ok || !result) throw new DesktopPaymentError(result?.error || "PAYMENT_PROVIDER_UNAVAILABLE");
  return result as T;
}

export function validPaymentURL(value: string, provider = "oxapay", sandbox = false): boolean {
  try {
    const url = new URL(value);
	if (url.protocol !== "https:" || url.username || url.password || url.port) return false;
	if (provider === "shopline") {
		const hosts = sandbox ? ["api-sandbox.shoplinepayments.com", "pay-sandbox.shoplinepayments.com"] : ["api.shoplinepayments.com", "pay.shoplinepayments.com"];
		return hosts.includes(url.hostname) && url.pathname === "/checkout/session" && !!url.searchParams.get("sessionToken") && !url.hash;
	}
	return provider === "oxapay" && ["oxapay.com", "pay.oxapay.com"].includes(url.hostname);
  } catch { return false; }
}

export function orderStorageKey(id: string): string { return `grabsilo.desktop.order.${id}`; }

export function paymentPrice(amount: number, currency: string, locale: string): string {
  if (currency === "USDT") return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 4 }).format(amount)} USDT`;
  if (currency === "TWD") return `NT$${new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(amount)}`;
  try { return new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits: 8 }).format(amount); }
  catch { return `${amount} ${currency}`; }
}

export function desktopProductPrice(product: DesktopPaymentProduct, locale: string): string {
  if (product.payment_currency === "USDT") {
    const amount = product.quote_amount ?? product.amount;
    const currency = product.quote_currency ?? product.currency;
    if (amount === undefined || !Number.isFinite(amount) || amount <= 0) return "";
    if (currency === "USDT") return paymentPrice(amount, "USDT", locale);
    // The server's frozen USD quote estimates USDT; OxaPay determines the
    // exact token amount. Never relabel the unconverted base price as USDT.
    if (currency === "USD") return `≈ ${paymentPrice(amount, "USDT", locale)}`;
    return "";
  }
  return product.amount !== undefined && product.currency ? paymentPrice(product.amount, product.currency, locale) : "";
}

export function desktopOrderPrice(order: DesktopPaymentOrder, locale: string): string {
  // Receipts use their stored invoice quote, never the current exchange rate
  // or the original internal TWD reference price.
  return paymentPrice(order.amount, order.currency, locale);
}
